import { CPENZLoader } from "@/components/CPENZLoader";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const IT = (localStorage.getItem("cpenz_lang") || "EN") === "IT";
const TXT = IT
  ? { wait: "Accesso in corso…", slow: "Sta impiegando più del previsto…", fail: "Non siamo riusciti a completare l'accesso con Google.", hint: "Su Safari prova a disattivare \"Impedisci monitoraggio cross-site\", oppure accedi con email e password.", retry: "Riprova", home: "Torna alla home" }
  : { wait: "Signing you in…", slow: "This is taking longer than expected…", fail: "We couldn't complete the Google sign-in.", hint: "On Safari, try disabling \"Prevent cross-site tracking\", or sign in with email and password.", retry: "Try again", home: "Back to home" };

export default function AuthCallback() {
  const navigate = useNavigate();
  const { finishGoogle } = useAuth();
  const processed = useRef(false);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    const qs = new URLSearchParams(window.location.search);
    const code = qs.get("code"), state = qs.get("state");
    const slowTimer = setTimeout(() => setStatus((s) => (s === "loading" ? "slow" : s)), 8000);

    const run = async () => {
      if (!sessionId && !code) {
        navigate("/", { replace: true });
        return;
      }
      try {
        const lang = localStorage.getItem("cpenz_lang") || "EN";
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const { data } = code
          ? await api.post("/auth/google/exchange", { code, state, redirect_uri: window.location.origin + "/auth/google", lang }, { timeout: 20000 })
          : await api.post("/auth/google/session", { session_id: sessionId, lang }, { timeout: 20000 });
        window.history.replaceState({}, document.title, "/app/dashboard");
        await finishGoogle(data);
        const pending = sessionStorage.getItem("cpenz_pending_preview");
        if (pending) {
          try {
            const p = JSON.parse(pending);
            const res = await api.post("/ideas/capture", { text: p.text, lang: p.lang, preview_id: p.preview_id });
            sessionStorage.removeItem("cpenz_pending_preview");
            navigate(`/app/idea/${res.data.id}`, { replace: true, state: { skipPreview: true } });
            return;
          } catch (err) { sessionStorage.removeItem("cpenz_pending_preview"); }
        }
        navigate("/app/home", { replace: true });
      } catch (e) {
        window.history.replaceState({}, document.title, "/app/dashboard");
        setStatus("error");
      } finally {
        clearTimeout(slowTimer);
      }
    };
    run();
    return () => clearTimeout(slowTimer);
  }, [navigate, finishGoogle]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="auth-callback-error">
        <p className="text-base font-semibold text-[#0b0f1a]">{TXT.fail}</p>
        <p className="text-sm text-slate-500 max-w-sm">{TXT.hint}</p>
        <div className="flex gap-3 mt-2">
          <Button data-testid="auth-callback-retry" onClick={() => { window.location.href = "/?login=google"; }} className="rounded-full bg-[#0b0f1a] text-white h-11 px-6">{TXT.retry}</Button>
          <Button data-testid="auth-callback-home" variant="outline" onClick={() => { window.location.href = "/"; }} className="rounded-full h-11 px-6">{TXT.home}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="auth-callback-loading">
      <CPENZLoader size={96} />
      <p className="text-sm text-slate-500">{status === "slow" ? TXT.slow : TXT.wait}</p>
    </div>
  );
}
